import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";

export default function Roster() {
  const [rows, setRows] = useState(null);
  const [employees, setEmployees] = useState({});   // candidate_id -> employee summary
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");
  const [busyId, setBusyId] = useState(null);
  const nav = useNavigate();

  const load = async () => {
    const r = await api.get("/roster");
    setRows(r.data);
    // Fetch employees best-effort; if MySQL is down we still show the roster.
    try {
      const emps = await api.get("/employees");
      const byCand = {};
      (emps.data || []).forEach((e) => { if (e.candidate_id) byCand[e.candidate_id] = e; });
      setEmployees(byCand);
    } catch (e) {
      // Roster stays usable; onboarding buttons will attempt creation on click.
      setEmployees({});
    }
  };

  useEffect(() => {
    (async () => {
      try {
        await load();
      } catch (e) {
        setErr(e?.response?.data?.detail || "Failed to load roster");
      }
    })();
  }, []);

  const startOnboarding = async (c) => {
    const existing = employees[c.id];
    if (existing) {
      nav(existing.onboarding_completed ? `/employees/${existing.id}` : `/employees/onboarding/${existing.id}`);
      return;
    }
    const employee_id = (c.offer?.employee_id || "").trim();
    if (!employee_id) {
      return toast.error("Employee ID missing on offer — set it before onboarding");
    }
    setBusyId(c.id);
    try {
      const r = await api.post("/employees", {
        candidate_id: c.id,
        employee_id,
        employee_name: c.full_name,
        date_of_joining: c.offer?.actual_joining_date || null,
        reporting_manager: c.offer?.reporting_manager || null,
        requisition_id: c.requisition_id || null,
      });
      toast.success("Onboarding started");
      nav(`/employees/onboarding/${r.data.id}`);
    } catch (e) {
      const s = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (s >= 500) {
        toast.error("Onboarding service is temporarily unavailable. Please retry in a moment.");
      } else {
        toast.error(detail || "Failed to start onboarding");
      }
    } finally {
      setBusyId(null);
    }
  };

  if (err) return <div className="p-8 text-error" data-testid="roster-error">{err}</div>;
  if (!rows) return <div className="p-8 text-quiet">Loading roster…</div>;

  const filtered = rows.filter((c) => {
    if (!q.trim()) return true;
    const s = q.trim().toLowerCase();
    return (
      (c.full_name || "").toLowerCase().includes(s)
      || (c.email || "").toLowerCase().includes(s)
      || (c.requisition_title || "").toLowerCase().includes(s)
      || (c.offer?.employee_id || "").toLowerCase().includes(s)
      || (c.offer?.reporting_manager || "").toLowerCase().includes(s)
    );
  });

  return (
    <div className="p-8 max-w-[1400px] mx-auto" data-testid="roster-page">
      <div className="border-b border-rule pb-6 flex items-end justify-between gap-6">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-quiet">Team · Onboarded</div>
          <h1 className="font-serif text-5xl text-ink mt-2 leading-none">Roster.</h1>
          <p className="text-sm text-quiet mt-3">
            {rows.length} joined {rows.length === 1 ? "hire" : "hires"} across all requisitions.
          </p>
        </div>
        <input
          data-testid="roster-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search name, email, role, employee-id…"
          className="w-96 border border-rule px-3 py-2 text-sm focus:outline-none focus:border-klein bg-white"
        />
      </div>

      <div className="mt-6 bg-white border border-rule overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-paper border-b border-rule">
            <tr>
              <Th>Employee</Th>
              <Th>Emp ID</Th>
              <Th>Role · Requisition</Th>
              <Th>Reporting manager</Th>
              <Th>Joined on</Th>
              <Th align="right">Offered CTC</Th>
              <Th />
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-quiet text-xs">
                {rows.length === 0 ? "No one has been marked as joined yet." : "No matches for that search."}
              </td></tr>
            )}
            {filtered.map((c) => (
              <tr key={c.id} className="border-b border-rule last:border-b-0 hover:bg-paper" data-testid={`roster-row-${c.id}`}>
                <Td>
                  <div className="font-medium text-ink">{c.full_name}</div>
                  <div className="text-xs text-quiet">{c.email}</div>
                </Td>
                <Td><span className="font-mono text-xs">{c.offer?.employee_id || "—"}</span></Td>
                <Td>
                  <div>{c.requisition_title}</div>
                  <div className="text-xs text-quiet font-mono">{c.requisition_code}</div>
                </Td>
                <Td>{c.offer?.reporting_manager || "—"}</Td>
                <Td>{c.offer?.actual_joining_date ? new Date(c.offer.actual_joining_date).toLocaleDateString() : "—"}</Td>
                <Td align="right">{c.offer?.offered_ctc ? `${c.offer.offered_ctc} LPA` : "—"}</Td>
                <Td>
                  <div className="flex items-center gap-3 justify-end">
                    <Link
                      to={`/candidates/${c.id}`}
                      data-testid={`roster-view-${c.id}`}
                      className="text-xs font-mono uppercase tracking-wider text-quiet hover:text-ink"
                    >
                      Candidate →
                    </Link>
                    {employees[c.id]?.onboarding_completed ? (
                      <Link
                        to={`/employees/${employees[c.id].id}`}
                        data-testid={`roster-employee-${c.id}`}
                        className="text-xs font-mono uppercase tracking-wider text-klein hover:underline"
                      >
                        View employee →
                      </Link>
                    ) : (
                      <button
                        data-testid={`roster-start-onboarding-${c.id}`}
                        disabled={busyId === c.id}
                        onClick={() => startOnboarding(c)}
                        className="bg-klein hover:bg-klein-hover text-white px-3 py-1.5 text-xs font-mono uppercase tracking-wider disabled:opacity-50"
                      >
                        {busyId === c.id ? "…" : employees[c.id] ? "Complete onboarding →" : "Start onboarding →"}
                      </button>
                    )}
                  </div>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const Th = ({ children, align = "left" }) => (
  <th className={`px-4 py-3 font-mono text-[10px] uppercase tracking-wider text-quiet text-${align}`}>{children}</th>
);
const Td = ({ children, align = "left" }) => (
  <td className={`px-4 py-3 text-ink text-${align}`}>{children}</td>
);
